import React from 'react';
import { formatForDisplay, localNativeFormat, parseLocalNative } from '../../utils/dateHelpers';

const Input = React.forwardRef(
  (
    {
      type = 'text',
      placeholder = '',
      value,
      onChange,
      disabled = false,
      error = '',
      label = '',
      className = '',
      centered = false,
      icon: Icon = null,
      iconPosition = 'left',
      onIconClick = null,
      rows = 3,
      variant = 'default', // 'default' | 'ghost' | 'ghost-sm' | 'date' | 'pill' | 'pillSquare'
      color = 'blue',
      displayFormat = 'MMM do yyyy HH:mm',
      ...props
    },
    ref
  ) => {
    const dateInputRef = React.useRef(null);
    const hasIcon = Icon !== null;
    const isIconClickable = onIconClick !== null;
    const isTextarea = type === 'textarea';
    const isDatePicker = type === 'date' || type === 'datetime-local';

    const getDisplayValue = () => {
      if (!isDatePicker || !value) return value;
      try { return formatForDisplay(value, displayFormat); } catch { return value; }
    };

    const getNativeValue = () => {
      if (!isDatePicker) return value ?? '';
      try { return localNativeFormat(value, type); } catch { return ''; }
    };

    const combinedRef = React.useCallback(
      (node) => {
        dateInputRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    const openDatePicker = () => {
      if (isDatePicker && dateInputRef.current) {
        try { dateInputRef.current.showPicker(); }
        catch { dateInputRef.current.focus(); }
      }
    };

    const handleIconClick = () => {
      if (isDatePicker) openDatePicker();
      else if (isIconClickable && onIconClick) onIconClick();
    };

    // Pill colour maps — token-aware
    const pillColors = {
      blue:   'bg-blue-100 text-blue-700',
      indigo: 'bg-blue-100 text-blue-700 border border-blue-300',
      slate:  '',
      white:  '',
    };

    const getInputClasses = () => {
      const iconPadL = hasIcon && iconPosition === 'left'  ? 'pl-10' : '';
      const iconPadR = hasIcon && iconPosition === 'right' ? 'pr-10' : '';
      const errRing  = error ? 'ring-2 ring-red-500 focus:ring-red-500' : '';
      const centred  = centered ? 'text-center placeholder:text-center' : '';

      if (variant === 'pill') {
        return `bg-transparent border-none outline-none rounded-full px-4 py-1.5 text-sm ${pillColors[color] || pillColors.blue} ${errRing} ${iconPadL} ${iconPadR} ${className}`;
      }
      if (variant === 'pillSquare') {
        return `bg-transparent border-none focus:bg-transparent outline-none rounded-lg px-4 py-1.5 text-sm ${pillColors[color] || pillColors.blue} ${errRing} ${iconPadL} ${iconPadR} ${className}`;
      }
      if (variant === 'ghost') {
        return `input-ghost ${centred} ${errRing} ${iconPadL} ${iconPadR} ${className}`;
      }
      if (variant === 'ghost-sm') {
        return `input-ghost-sm ${centred} ${errRing} ${iconPadL} ${iconPadR} ${className}`;
      }
      if (variant === 'date') {
        return `input-date ${centred} ${errRing} ${iconPadL} ${iconPadR} ${className}`;
      }
      return `input ${centred} ${errRing} ${iconPadL} ${iconPadR} ${className}`;
    };

    const inputClasses = getInputClasses();

    return (
      <div className='w-full'>
        {label && (
          <label className='block text-sm font-medium mb-1.5' style={{ color: 'var(--text-secondary)' }}>
            {label}
          </label>
        )}
        <div className='relative'>
          {hasIcon && iconPosition === 'left' && !isTextarea && (
            <div
              className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                isDatePicker || isIconClickable ? 'cursor-pointer' : ''
              }`}
              style={{ color: isDatePicker || isIconClickable ? 'var(--brand)' : 'var(--text-ghost)' }}
              onClick={handleIconClick}
            >
              <Icon className='w-4.5 h-4.5' style={{ width: '1.125rem', height: '1.125rem' }} />
            </div>
          )}

          {isTextarea ? (
            <textarea
              ref={ref}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              disabled={disabled}
              rows={rows}
              className={inputClasses}
              {...props}
            />
          ) : isDatePicker ? (
            <>
              <input
                type='text'
                placeholder={placeholder}
                value={getDisplayValue() || ''}
                readOnly
                disabled={disabled}
                onClick={openDatePicker}
                className={`${inputClasses} cursor-pointer`}
                {...props}
              />
              <input
                ref={combinedRef}
                type={type}
                value={getNativeValue()}
                onChange={(e) => {
                  const parsed = parseLocalNative(e.target.value, type);
                  onChange?.(parsed ?? e);
                }}
                disabled={disabled}
                tabIndex={-1}
                className='absolute inset-0 w-0 h-0 opacity-0 pointer-events-none'
              />
            </>
          ) : (
            <input
              ref={ref}
              type={type}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              disabled={disabled}
              className={inputClasses}
              {...props}
            />
          )}

          {hasIcon && iconPosition === 'right' && !isTextarea && (
            <div
              className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                isDatePicker || isIconClickable ? 'cursor-pointer' : ''
              }`}
              style={{ color: isDatePicker || isIconClickable ? 'var(--brand)' : 'var(--text-ghost)' }}
              onClick={handleIconClick}
            >
              <Icon className='w-4.5 h-4.5' style={{ width: '1.125rem', height: '1.125rem' }} />
            </div>
          )}
        </div>
        {error && (
          <p className='mt-1.5 text-xs' style={{ color: '#ef4444' }}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
